'use client';

import { useCallback, useEffect, useState } from 'react';

import { SESSION_KEYS } from '@/core/utils/constants';

/**
 * Define la estructura del estado de los pedidos.
 *
 * @property docType - Tipo de documento para la búsqueda.
 *
 * @property docId - ID del documento para la búsqueda.
 *
 * @property purchaseOrder - Orden de compra.
 *
 * @property creditAccount - Cuenta de crédito.
 *
 * @property orderCreationDateSince - Fecha de creación del pedido (desde).
 *
 * @property orderCreationDateUntil - Fecha de creación del pedido (hasta).
 *
 * @property deliveryPromiseDateSince - Fecha de promesa de entrega (desde).
 *
 * @property deliveryPromiseDateUntil - Fecha de promesa de entrega (hasta).
 *
 * @property deliveryPlanType - Tipos de plan de entrega seleccionados.
 *
 * @property selectedStores - Tiendas seleccionadas.
 *
 * @property currentPage - Página actual de la paginación.
 *
 * @property pageSize - Número de elementos por página.
 *
 * @property advancedVisible - Visibilidad de los filtros avanzados.
 *
 * @property filterCount - Número de filtros aplicados.
 *
 * @property hasTriedInvalidSearch - Indica si se ha intentado una búsqueda inválida.
 *
 * @property shouldSearch - Indica si se debe realizar una búsqueda.
 *
 * @property isManualSearch - Indica si la búsqueda es manual (botón Aplicar).
 */
export interface OrdersState {
  docType: string;
  docId: string;
  purchaseOrder?: string;
  creditAccount?: string;
  orderCreationDateSince?: string;
  orderCreationDateUntil?: string;
  deliveryPromiseDateSince?: string;
  deliveryPromiseDateUntil?: string;
  deliveryPlanType?: string[];
  selectedStores: string[];

  currentPage: number;
  pageSize: number;

  advancedVisible: boolean;
  filterCount: number;
  hasTriedInvalidSearch: boolean;

  shouldSearch: boolean;
  isManualSearch: boolean; // Indica si la búsqueda es manual (botón Aplicar)
}

/**
 * Define la estructura del hook para gestionar el estado de los pedidos.
 *
 * @property state - El estado actual de los pedidos.
 *
 * @property updateFilters - Función para actualizar los filtros.
 *
 * @property updatePagination - Función para actualizar la paginación.
 *
 * @property resetFilters - Función para reiniciar los filtros.
 *
 * @property resetToIdOnly - Función para reiniciar los filtros a solo ID.
 *
 * @property toggleAdvanced - Función para mostrar/ocultar los filtros avanzados.
 *
 * @property triggerSearch - Función para disparar una búsqueda.
 *
 * @property resetSearchFlag - Función para reiniciar el flag de búsqueda.
 */
export interface OrdersStateHook {
  state: OrdersState;
  updateFilters: (filters: Partial<OrdersState>) => void;
  updatePagination: (page: number, pageSize?: number) => void;
  resetFilters: () => void;
  resetToIdOnly: (idType: string, idValue: string) => void;
  toggleAdvanced: (visible?: boolean) => void;
  triggerSearch: (filters?: Partial<OrdersState>) => void;
  resetSearchFlag: () => void;
}

const DEFAULT_STATE: OrdersState = {
  docType: 'factura',
  docId: '',
  purchaseOrder: undefined,
  creditAccount: undefined,
  orderCreationDateSince: undefined,
  orderCreationDateUntil: undefined,
  deliveryPromiseDateSince: undefined,
  deliveryPromiseDateUntil: undefined,
  deliveryPlanType: undefined,
  selectedStores: [],
  currentPage: 1,
  pageSize: 10,
  advancedVisible: false,
  filterCount: 0,
  hasTriedInvalidSearch: false,
  shouldSearch: false,
  isManualSearch: false
};

/**
 * Hook personalizado para gestionar el estado de la página de pedidos.
 * Persiste el estado en `sessionStorage`.
 *
 * @returns Un objeto con el estado y las funciones para actualizarlo.
 */
export function useOrdersState(initialState?: OrdersState): OrdersStateHook {
  const [state, setState] = useState<OrdersState>(() => {
    if (initialState) return initialState;
    return DEFAULT_STATE;
  });

  useEffect(() => {
    // Si ya recibimos un estado inicial del servidor (SSR), no lo sobreescribimos
    // con lo que hay en sessionStorage para evitar inconsistencias en el flujo,
    // o bien podríamos combinarlo si fuera requerido.
    // En este caso, asumimos que si no hay initialState, podemos leer de sesión.
    if (initialState) return;

    try {
      const stored = sessionStorage.getItem(SESSION_KEYS.ORDERS_STATE);
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.shouldSearch = true;
        setState({
          ...DEFAULT_STATE,
          ...parsed,
          hasTriedInvalidSearch: false
        });
      }
    } catch (error) {
      console.error('Error reading orders state from sessionStorage:', error);
    }
  }, [initialState]);

  const saveToStorage = useCallback((newState: OrdersState) => {
    if (typeof window === 'undefined') return;

    try {
      sessionStorage.setItem(SESSION_KEYS.ORDERS_STATE, JSON.stringify(newState));
    } catch (error) {
      console.error('Error saving orders state to sessionStorage:', error);
    }
  }, []);

  const updateState = useCallback(
    (updates: Partial<OrdersState>) => {
      setState((prev) => {
        const newState = { ...prev, ...updates };

        let count = 0;
        if (newState.purchaseOrder) count++;
        if (newState.creditAccount) count++;
        if (newState.orderCreationDateSince && newState.orderCreationDateUntil) count++;
        if (newState.deliveryPromiseDateSince && newState.deliveryPromiseDateUntil) count++;
        if (newState.deliveryPlanType && newState.deliveryPlanType.length > 0) count++;
        if (newState.selectedStores.length > 0) count += newState.selectedStores.length;

        const finalState = { ...newState, filterCount: count };
        saveToStorage(finalState);
        return finalState;
      });
    },
    [saveToStorage]
  );

  /**
   * Actualiza los filtros y reinicia la paginación.
   *
   * @param filters - Objeto con los filtros a actualizar.
   */
  const updateFilters = useCallback(
    (filters: Partial<OrdersState>) => {
      updateState({
        ...filters,
        currentPage: 1,
        ...(filters.hasTriedInvalidSearch === undefined && { hasTriedInvalidSearch: false })
      });
    },
    [updateState]
  );

  /**
   * Actualiza la paginación y dispara una búsqueda.
   *
   * @param page - Nuevo número de página.
   *
   * @param pageSize - (Opcional) Nuevo tamaño de página.
   */
  const updatePagination = useCallback(
    (page: number, pageSize?: number) => {
      updateState({
        currentPage: page,
        ...(pageSize && { pageSize }),
        shouldSearch: true
      });
    },
    [updateState]
  );

  /**
   * Reinicia los filtros a sus valores por defecto.
   */
  const resetFilters = useCallback(() => {
    updateState({
      purchaseOrder: undefined,
      creditAccount: undefined,
      orderCreationDateSince: undefined,
      orderCreationDateUntil: undefined,
      deliveryPromiseDateSince: undefined,
      deliveryPromiseDateUntil: undefined,
      selectedStores: [],
      currentPage: 1,
      filterCount: 0,
      advancedVisible: true,
      hasTriedInvalidSearch: false
    });
  }, [updateState]);

  /**
   * Reinicia los filtros para buscar solo por tipo y valor de ID.
   *
   * @param idType - Tipo de ID.
   *
   * @param idValue - Valor del ID.
   */
  const resetToIdOnly = useCallback(
    (idType: string, idValue: string) => {
      updateState({
        docType: idType,
        docId: idValue,
        purchaseOrder: undefined,
        creditAccount: undefined,
        orderCreationDateSince: undefined,
        orderCreationDateUntil: undefined,
        deliveryPromiseDateSince: undefined,
        deliveryPromiseDateUntil: undefined,
        selectedStores: [],
        currentPage: 1,
        filterCount: 0,
        advancedVisible: false,
        hasTriedInvalidSearch: false
      });
    },
    [updateState]
  );

  /**
   * Muestra u oculta los filtros avanzados.
   *
   * @param visible - (Opcional) Estado de visibilidad.
   */
  const toggleAdvanced = useCallback(
    (visible?: boolean) => {
      updateState({
        advancedVisible: visible !== undefined ? visible : !state.advancedVisible
      });
    },
    [updateState, state.advancedVisible]
  );

  /**
   * Dispara una búsqueda con los filtros actuales.
   *
   * @param filters - (Opcional) Filtros adicionales a aplicar.
   *
   * @param isManual - Indica si la búsqueda es manual.
   */
  const triggerSearch = useCallback(
    (filters?: Partial<OrdersState>, isManual = false) => {
      updateState({
        ...filters,
        shouldSearch: true,
        isManualSearch: isManual
      });
    },
    [updateState]
  );

  /**
   * Reinicia el flag de búsqueda para evitar búsquedas no deseadas.
   */
  const resetSearchFlag = useCallback(() => {
    updateState({ shouldSearch: false });
  }, [updateState]);

  return {
    state,
    updateFilters,
    updatePagination,
    resetFilters,
    resetToIdOnly,
    toggleAdvanced,
    triggerSearch,
    resetSearchFlag
  };
}
